/**
 * DraggableModule Component
 * Interactive draggable solar module visualization
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 1026-1129)
 * Handles module rendering, selection, and drag-and-drop functionality
 */

import React, { useState, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { Polygon } from 'react-leaflet';
import L from 'leaflet';
import { createModuleRectangle } from '../../../utils/modules';

export interface SolarModule {
  position: [number, number];
  width: number;
  height: number;
  orientation: 'portrait' | 'landscape';
  rotation?: number;
  segmentId?: number;
}

export interface DraggableModuleProps {
  module: SolarModule;
  isSelected: boolean;
  isInteractive?: boolean;
  opacity?: number;
  onSelect: () => void;
  onMove: (newPosition: [number, number]) => void;
  onClick: (e: any) => void;
}

/**
 * Interactive draggable solar module component
 * Features:
 * - Visual feedback for selection state
 * - Mouse-based dragging with map interaction disabled during drag
 * - Alt+drag compatibility for multi-select
 * - Uses custom 'modulesPane' with z-index 500
 */
export function DraggableModule({
  module,
  isSelected,
  isInteractive = true,
  opacity = 0.7,
  onSelect,
  onMove,
  onClick,
}: DraggableModuleProps) {
  const map = useMap();
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef<[number, number] | null>(null);

  const moduleRect = createModuleRectangle(
    module.position,
    module.width,
    module.height,
    module.orientation,
    module.rotation || 0
  );

  const handleMouseDown = (e: any) => {
    if (e.originalEvent.button !== 0) return; // Only left click
    if (!isInteractive) return; // Don't allow dragging in Site mode

    // If Alt key is pressed, allow event to propagate for drag-select
    if (e.originalEvent.altKey) {
      return;
    }

    setIsDragging(true);
    dragOffsetRef.current = [e.latlng.lat, e.latlng.lng];
    onSelect();
    map.dragging.disable();
    L.DomEvent.stopPropagation(e);
  };

  useEffect(() => {
    if (!isDragging || !dragOffsetRef.current) return;

    const handleMouseMove = (e: any) => {
      if (!dragOffsetRef.current) return;

      const [startLat, startLng] = dragOffsetRef.current;
      const deltaLat = e.latlng.lat - startLat;
      const deltaLng = e.latlng.lng - startLng;

      const newPosition: [number, number] = [
        module.position[0] + deltaLat,
        module.position[1] + deltaLng,
      ];

      onMove(newPosition);
      dragOffsetRef.current = [e.latlng.lat, e.latlng.lng];
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragOffsetRef.current = null;
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };

    map.on('mousemove', handleMouseMove);
    map.on('mouseup', handleMouseUp);

    return () => {
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };
  }, [isDragging, map, module.position, onMove]);

  // Normalize module rectangle coordinates to prevent floating point precision issues at high zoom
  const normalizedRect = moduleRect.map(([lat, lng]) => [
    Math.round(lat * 1e8) / 1e8,
    Math.round(lng * 1e8) / 1e8
  ] as [number, number]);

  return (
    <Polygon
      positions={normalizedRect}
      pathOptions={{
        color: isSelected ? '#fbbf24' : (isInteractive ? '#1e40af' : '#64748b'),  // Darker blue border (#1e40af = blue-800)
        fillColor: isSelected ? '#fde047' : (isInteractive ? '#2563eb' : '#94a3b8'),  // Darker blue fill (#2563eb = blue-600)
        fillOpacity: opacity,
        weight: isSelected ? 3 : 2,
      }}
      // @ts-ignore - react-leaflet v5 supports pane but v2 types don't
      pane="modulesPane"  // Use custom pane with z-index 500 to ensure modules are always on top
      eventHandlers={{
        mousedown: isInteractive ? handleMouseDown : undefined,
        click: isInteractive ? (e: any) => {
          // If Alt key is pressed, allow event to propagate for drag-select
          if (!e.originalEvent?.altKey) {
            L.DomEvent.stopPropagation(e);
            onClick(e);
          }
        } : undefined,
      }}
    />
  );
}
